<div class="modal-header">
  <h2 class="modal-title fw-bold">{{ 'manageEvent.editServiceRegistration.title' | translate }}</h2>
  <button type="button" class="btn-close" (click)="close()"></button>
</div>

<div class="modal-body">
  <form [formGroup]="form">
    <!-- Current Registration Info -->
    <div class="alert alert-info d-flex align-items-start mb-6">
      <i class="fa fa-info-circle fs-2x me-3 mt-1"></i>
      <div>
        <div class="fw-bold mb-2">{{ 'manageEvent.editServiceRegistration.currentRegistration' | translate }}</div>
        <div class="mb-1">
          <strong>{{ 'manageEvent.editServiceRegistration.user' | translate }}:</strong>
          @if (registration.user) {
            {{ registration.user.first_name }} {{ registration.user.last_name }}
            @if (registration.user.email) {
              <span class="text-muted">({{ registration.user.email }})</span>
            }
          } @else {
            <span class="text-muted">{{ 'common.notAvailable' | translate }}</span>
          }
        </div>
        <div>
          <strong>{{ 'manageEvent.editServiceRegistration.numRegistrations' | translate }}:</strong>
          <span class="badge badge-light-primary ms-2">{{ registration.num_registrations }}</span>
        </div>
      </div>
    </div>

    <!-- User Search Field -->
    <div class="mb-5">
      <label class="form-label required">{{ 'manageEvent.editServiceRegistration.user' | translate }}</label>
      <div class="position-relative">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            [(ngModel)]="userSearchTerm"
            [ngModelOptions]="{standalone: true}"
            (input)="onUserSearch()"
            [placeholder]="'manageEvent.editServiceRegistration.searchPlaceholder' | translate"
            [class.is-invalid]="userIdInvalid"
          />
          @if (selectedUser) {
            <button
              class="btn btn-outline-secondary"
              type="button"
              (click)="clearUserSearch()"
            >
              <i class="fa fa-times"></i>
            </button>
          }
        </div>
        @if (userIdInvalid) {
          <div class="text-danger fs-7 mt-1">{{ 'validation.userRequired' | translate }}</div>
        }

        <!-- Loading indicator -->
        @if (isSearchingUsers) {
          <div class="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-3 text-center" style="z-index: 1000;">
            <span class="spinner-border spinner-border-sm text-primary me-2"></span>
            {{ 'common.searching' | translate }}
          </div>
        }

        <!-- User Dropdown -->
        @if (userSearchResults.length > 0 && userSearchTerm && !selectedUser && !isSearchingUsers) {
          <div class="user-dropdown position-absolute w-100 bg-white border rounded shadow-sm mt-1" style="z-index: 1000; max-height: 300px; overflow-y: auto;">
            @for (user of userSearchResults; track user.user_id) {
              <div
                class="user-item p-3 cursor-pointer hover-bg-light border-bottom"
                (click)="selectUser(user)"
              >
                <div class="fw-semibold">{{ user.full_name || (user.first_name + ' ' + user.last_name) }}</div>
                @if (user.email) {
                  <div class="text-muted fs-7">{{ user.email }}</div>
                }
              </div>
            }
          </div>
        }

        <!-- No results -->
        @if (userSearchResults.length === 0 && userSearchTerm.length >= 2 && !selectedUser && !isSearchingUsers) {
          <div class="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-3 text-center text-muted" style="z-index: 1000;">
            {{ 'common.noResults' | translate }}
          </div>
        }

        <!-- Selected User Display -->
        @if (selectedUser) {
          <div class="mt-3 p-4 bg-light rounded">
            <div class="fw-bold mb-2">{{ 'manageEvent.editServiceRegistration.selectedUser' | translate }}</div>
            <div class="row g-3">
              <div class="col-md-6">
                <div class="text-muted fs-7">{{ 'manageEvent.editServiceRegistration.name' | translate }}</div>
                <div class="fw-semibold">{{ selectedUser.first_name }} {{ selectedUser.last_name }}</div>
              </div>
              @if (selectedUser.email) {
                <div class="col-md-6">
                  <div class="text-muted fs-7">{{ 'manageEvent.editServiceRegistration.email' | translate }}</div>
                  <div class="fw-semibold">{{ selectedUser.email }}</div>
                </div>
              }
              <div class="col-md-6">
                <div class="text-muted fs-7">{{ 'manageEvent.editServiceRegistration.userId' | translate }}</div>
                <div class="fw-semibold">{{ selectedUser.user_id }}</div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Number of Registrations -->
    <div class="mb-5">
      <label class="form-label required">{{ 'manageEvent.editServiceRegistration.numRegistrations' | translate }}</label>
      <input
        type="number"
        class="form-control"
        formControlName="num_registrations"
        [placeholder]="'manageEvent.editServiceRegistration.numPlaceholder' | translate"
        min="1"
        [class.is-invalid]="form.get('num_registrations')?.invalid && form.get('num_registrations')?.touched"
      />
      @if (form.get('num_registrations')?.invalid && form.get('num_registrations')?.touched) {
        <div class="text-danger fs-7 mt-1">
          @if (form.get('num_registrations')?.errors?.['required']) {
            {{ 'validation.numberRequired' | translate }}
          }
          @if (form.get('num_registrations')?.errors?.['min']) {
            {{ 'validation.minOne' | translate }}
          }
        </div>
      }
    </div>

    <!-- Notes Field -->
    <div class="mb-5">
      <label class="form-label">{{ 'editRegistration.notes' | translate }}</label>
      <textarea
        class="form-control"
        formControlName="notes"
        rows="3"
        [placeholder]="'editRegistration.notesPlaceholder' | translate"
      ></textarea>
      <div class="text-muted fs-7 mt-1">{{ 'editRegistration.notesHint' | translate }}</div>
    </div>
  </form>
</div>

<div class="modal-footer">
  <app-button
    [variant]="'outline'"
    [label]="'common.cancel' | translate"
    (clicked)="close()"
    [disabled]="saving"
  />
  <app-button
    [variant]="'primary'"
    [label]="saving ? ('common.saving' | translate) : ('common.saveChanges' | translate)"
    [iconClass]="saving ? 'fa fa-spinner fa-spin' : 'fa fa-save'"
    (clicked)="save()"
    [disabled]="saving || form.invalid"
  />
</div>
